import { useMemo, useState } from "react";
import { Button, Modal, Select } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  ParameterConfigurationOption,
  filterConfigsForModel,
  formatConfigOptionLabel,
  getCommissioningTipForLanguage,
} from "../../lib/parameterConfiguration";
import {
  hasCommissioningTip,
  sanitizeCommissioningTipHtml,
} from "../../lib/sanitizeHtml";
import "./parameterConfigurationSelect.scss";

type Props = {
  configs: ParameterConfigurationOption[];
  solisecoModel?: string;
  currentConfigId?: number | null;
  value: number | null;
  onChange: (configId: number) => void;
  disabled?: boolean;
};

const COMMISSIONING_TIP_Z_INDEX = 1400;

export const CommissioningTipModal = ({
  open,
  title,
  tip,
  closeLabel,
  onClose,
}: {
  open: boolean;
  title: string;
  tip: string;
  closeLabel: string;
  onClose: () => void;
}) => {
  const html = sanitizeCommissioningTipHtml(tip);
  return (
    <Modal
      open={open}
      title={title}
      onCancel={onClose}
      footer={<Button onClick={onClose}>{closeLabel}</Button>}
      destroyOnClose
      getContainer={() => document.body}
      zIndex={COMMISSIONING_TIP_Z_INDEX}
      centered
      styles={{
        body: {
          maxHeight: "min(60vh, 480px)",
          overflowY: "auto",
          overscrollBehavior: "contain",
        },
      }}
    >
      <div
        className="commissioning-tip-content"
        style={{ overflowWrap: "anywhere" }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </Modal>
  );
};

const ParameterConfigurationPicker = ({
  configs,
  solisecoModel,
  currentConfigId,
  value,
  onChange,
  disabled = false,
}: Props) => {
  const { t, i18n } = useTranslation("home");
  const [tipOpen, setTipOpen] = useState(false);

  const options = useMemo(
    () => filterConfigsForModel(configs, solisecoModel, currentConfigId),
    [configs, solisecoModel, currentConfigId]
  );

  const selected = options.find((config) => config.id === value) ?? null;
  const tip = getCommissioningTipForLanguage(selected, i18n.language);

  return (
    <div className="parameter-configuration-picker">
      <div className="input-block">
        <label>{t("wizard_config_label")}</label>
        <Select
          value={value ?? undefined}
          onChange={(id) => onChange(id)}
          disabled={disabled || options.length === 0}
          placeholder={t("wizard_config_placeholder")}
          className="parameter-configuration-select"
          popupClassName="parameter-configuration-select-dropdown"
          popupMatchSelectWidth={false}
          optionLabelProp="label"
        >
          {options.map((config) => (
            <Select.Option
              key={config.id}
              value={config.id}
              label={formatConfigOptionLabel(config)}
            >
              {formatConfigOptionLabel(config)}
            </Select.Option>
          ))}
        </Select>
      </div>
      {hasCommissioningTip(tip) && (
        <Button
          type="link"
          icon={<QuestionCircleOutlined />}
          onClick={() => setTipOpen(true)}
          style={{ paddingLeft: 0 }}
        >
          {t("wizard_config_tip_button")}
        </Button>
      )}
      <CommissioningTipModal
        open={tipOpen}
        title={selected ? formatConfigOptionLabel(selected) : t("wizard_config_tip_title")}
        tip={tip}
        closeLabel={t("wizard_config_tip_close")}
        onClose={() => setTipOpen(false)}
      />
    </div>
  );
};

export default ParameterConfigurationPicker;
